import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { ReactNode } from "react";
import { X } from "lucide-react";
import type { EconHeaderCountdownEvent } from "../../types/econ-header";
import { EconFlagIcon } from "../../lib/econ-flag-bank";
import {
  displayHeaderForecast,
  displayHeaderValue,
  econHeaderActualPrintKey,
  formatHeaderCountdown,
  formatHeaderReleaseVerdict,
  getEconHeaderCountdownPulseMode,
  hasEconHeaderActual,
  shouldRevealEconHeaderPrint,
} from "../../lib/econ-header-countdown";
import {
  countdownPulseColor,
  ensureEconHeaderCountdownStyles,
  getCountdownOverlayAnimation,
} from "./econ-header-countdown-animation";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface EconHeaderCountdownPillProps {
  event: EconHeaderCountdownEvent;
  baseRimColor?: string;
  forceExpanded?: boolean;
  ivIntro?: ReactNode;
  onDismiss?: () => void;
}

export function EconHeaderCountdownPill({
  event,
  baseRimColor = "rgba(199, 159, 74, 0.22)",
  forceExpanded = false,
  ivIntro,
  onDismiss,
}: EconHeaderCountdownPillProps) {
  ensureEconHeaderCountdownStyles();
  const [expanded, setExpanded] = useState(forceExpanded);
  const [now, setNow] = useState(() => Date.now());
  const hasIvIntro = Boolean(ivIntro);
  const [introVisible, setIntroVisible] = useState(hasIvIntro);
  const [introMounted, setIntroMounted] = useState(hasIvIntro);
  const [econVisible, setEconVisible] = useState(!hasIvIntro);
  const [shellWide, setShellWide] = useState(!hasIvIntro);
  const [pulseKey, setPulseKey] = useState(0);
  const [releaseFadeKey, setReleaseFadeKey] = useState(0);
  const lastPulseSecondRef = useRef<number | null>(null);
  const revealedPrintKeyRef = useRef<string | null>(null);
  const actualPrintKey = econHeaderActualPrintKey(event);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!hasIvIntro) {
      setIntroVisible(false);
      setIntroMounted(false);
      setEconVisible(true);
      setShellWide(true);
      return;
    }
    setIntroMounted(true);
    setIntroVisible(true);
    setEconVisible(false);
    setShellWide(false);
    const fadeOut = window.setTimeout(() => setIntroVisible(false), 120);
    const fadeIn = window.setTimeout(() => setEconVisible(true), 420);
    const widen = window.setTimeout(() => setShellWide(true), 760);
    const unmount = window.setTimeout(() => setIntroMounted(false), 760);
    return () => {
      window.clearTimeout(fadeOut);
      window.clearTimeout(fadeIn);
      window.clearTimeout(widen);
      window.clearTimeout(unmount);
    };
  }, [event.id, hasIvIntro]);

  const countdown = formatHeaderCountdown(event.scheduledAt, now);
  const countdownMs = Math.max(0, new Date(event.scheduledAt).getTime() - now);
  const secondsRemaining = Math.ceil(countdownMs / 1000);
  const hasActual = hasEconHeaderActual(event.actualValue);
  const pulseMode = getEconHeaderCountdownPulseMode({
    secondsRemaining,
    hasActual,
  });
  const isAwaitingActual = pulseMode === "awaiting";
  const rimColor = event.importanceColor || baseRimColor;
  const displayActual = displayHeaderValue(event.actualValue);
  const displayForecast = displayHeaderForecast(event.deskForecast);
  const releaseVerdict = formatHeaderReleaseVerdict(event);
  const statusText = hasActual ? releaseVerdict : countdown;
  const pulseColor = countdownPulseColor(secondsRemaining);
  const overlayAnimation = getCountdownOverlayAnimation({
    pulseMode,
    pulseKey,
    releaseFadeKey,
  });

  useEffect(() => {
    lastPulseSecondRef.current = null;
    setPulseKey(0);
    setReleaseFadeKey(0);
  }, [event.id]);

  useEffect(() => {
    if (
      shouldRevealEconHeaderPrint({
        actualPrintKey,
        revealedPrintKey: revealedPrintKeyRef.current,
        forceExpanded,
      })
    ) {
      setExpanded(true);
      revealedPrintKeyRef.current = actualPrintKey;
      setReleaseFadeKey((value) => value + 1);
    }
    if (
      pulseMode === "threshold" &&
      lastPulseSecondRef.current !== secondsRemaining
    ) {
      setPulseKey((value) => value + 1);
      lastPulseSecondRef.current = secondsRemaining;
    }
    if (pulseMode !== "threshold") lastPulseSecondRef.current = null;
  }, [actualPrintKey, forceExpanded, pulseMode, secondsRemaining]);

  return (
    <div
      className="group/econ-pill relative inline-flex max-w-[calc(100vw-24px)] overflow-visible transition-[width] duration-300"
      style={
        {
          width: shellWide ? "min(360px, calc(100vw - 24px))" : "194px",
        } as CSSProperties
      }
    >
      <button
        type="button"
        data-testid="econ-header-countdown-pill"
        aria-expanded={expanded}
        aria-controls="econ-header-countdown-drawer"
        onClick={() => setExpanded((value) => !value)}
        className="relative z-[90] h-7 w-full overflow-hidden rounded-lg border bg-[var(--fintheon-bg)]/92 pl-3 pr-7 text-left backdrop-blur-md transition-[border-color,box-shadow,opacity,transform] duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55"
        style={
          {
            "--econ-rim": rimColor,
            animation: isAwaitingActual
              ? "econ-header-actual-pulse 900ms ease-in-out infinite"
              : undefined,
            borderColor: rimColor,
            boxShadow: isAwaitingActual
              ? undefined
              : `0 0 0 1px color-mix(in srgb, ${rimColor} 18%, transparent)`,
          } as CSSProperties
        }
      >
        <span
          key={overlayAnimation.key}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-0"
          style={
            {
              "--econ-pulse-color": pulseColor,
              animation: overlayAnimation.animation,
            } as CSSProperties
          }
        />
        {introMounted ? (
          <div
            className={`pointer-events-none absolute left-3 right-3 top-0 flex h-7 items-center transition-opacity duration-300 ${
              introVisible ? "opacity-100" : "opacity-0"
            }`}
          >
            {ivIntro}
          </div>
        ) : null}
        <div
          className={`grid h-7 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 transition-opacity duration-300 ${
            econVisible ? "opacity-100" : "opacity-0"
          }`}
        >
          <EconFlagIcon country={event.country ?? event.countryFlag} />
          <span className="min-w-0 truncate text-[9px] font-semibold text-[var(--fintheon-accent)]/88">
            {event.eventName}
          </span>
          <span
            className={`min-w-[46px] text-left font-mono text-[10px] tabular-nums text-[var(--fintheon-text)]/78 ${
              hasActual ? "" : "rounded-[3px] px-1.5 py-0.5"
            }`}
          >
            <FintheonNumericText
              text={statusText}
              kind={hasActual ? "econ-actual" : "count"}
              surface="econ-countdown-drawer"
            />
          </span>
        </div>
      </button>
      {onDismiss ? (
        <button
          type="button"
          aria-label={`Dismiss ${event.eventName}`}
          title={`Dismiss ${event.eventName}`}
          onClick={onDismiss}
          className="absolute right-1.5 top-1/2 z-[95] flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full text-[var(--fintheon-text)]/58 opacity-0 transition-[opacity,color,transform] duration-200 hover:text-[var(--fintheon-text)] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55 group-hover/econ-pill:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
      <div
        id="econ-header-countdown-drawer"
        data-testid="econ-header-countdown-drawer"
        data-econ-actual-print={actualPrintKey ?? undefined}
        aria-hidden={!expanded}
        className={`absolute left-1/2 top-[calc(100%-1px)] z-[80] w-[90%] -translate-x-1/2 rounded-b-lg rounded-t-[3px] border border-[var(--fintheon-accent)]/14 bg-[var(--fintheon-bg)]/96 px-2 pb-1.5 pt-[11px] shadow-[0_10px_22px_rgba(0,0,0,0.42)] backdrop-blur-md transition-[opacity,transform,filter] duration-260 ${
          expanded
            ? "pointer-events-auto translate-y-0 opacity-100 blur-0"
            : "pointer-events-none -translate-y-1 opacity-0 blur-[1px]"
        }`}
      >
        <div className="grid grid-cols-3 gap-3">
          <ValueCell
            label="Prev"
            value={displayHeaderValue(event.previousValue)}
          />
          <ValueCell label="Forecast" value={displayForecast} />
          <ValueCell
            label="Actual"
            value={displayActual}
            accent={hasActual}
            revealKey={hasActual ? String(event.actualValue) : null}
          />
        </div>
      </div>
    </div>
  );
}

function ValueCell({
  label,
  value,
  accent = false,
  revealKey = null,
}: {
  label: string;
  value: string;
  accent?: boolean;
  revealKey?: string | null;
}) {
  return (
    <span className="flex min-w-0 items-baseline justify-between gap-2">
      <span className="text-[9px] text-[var(--fintheon-muted)]">{label}:</span>
      <span
        key={revealKey ?? value}
        className={`truncate font-mono text-[10px] tabular-nums ${
          accent
            ? "text-[var(--fintheon-accent)]"
            : "text-[var(--fintheon-text)]/76"
        }`}
        style={
          revealKey
            ? { animation: "econ-header-actual-value-in 320ms ease-out 1" }
            : undefined
        }
      >
        <FintheonNumericText
          text={value}
          kind={label === "Actual" ? "econ-actual" : "metric"}
          surface="econ-countdown-drawer"
        />
      </span>
    </span>
  );
}
